import { match } from "ts-pattern";
import type { z } from "zod";
import type { Furniture } from "../../../../../apps/frontend/src/graphql/genql/magento";
import type { CabinetState } from "../../context/StoreContext";
import { LayoutModus } from "../../hooks/useCanvasStore";
import { materialsSku } from "../../library/utilities";
import {
	CompartmentPosition,
	type CompartmentType,
	compartmentsOnSameX,
	compartmentsOnSameY,
	getMinSize,
	isNextToCompartmentOnX,
	isNextToCompartmentOnY,
	updateCompartment,
} from "./data/Compartment";
import type { DoorSide } from "./data/Door";
import { MAX_DOUBLE_DOOR_WIDTH } from "./data/DoubleDoor";
import type { Horizontal } from "./data/Horizontal";
import { getMaxShelfAmount } from "./data/Shelf";
import { type Vertical, verticalsFromCompartment } from "./data/Vertical";
import type { CabinetSchema } from "./schema";
import { IdPrefix, generateId } from "./utils";

export type CabinetSchemaType = z.infer<typeof CabinetSchema>;
export type ApiData = Furniture;
export type ObjectList<T = object> = Map<string, T>;

const CURRENT_VERSION = 4;

export const CORNER_SMOOTHING = 4;
const SHELF_OFFSET_WITH_DOOR = 200;
const SHELF_DISTANCE = 32;
export const VERTICAL_BLIND_THRESHOLD = 600;
const MIN_HEIGHT_FOR_VERTICAL = 150;

export function defaultState(
	width: number,
	height: number,
	depth: number,
	thickness: number,
	material: string,
	api?: ApiData,
) {
	const baseState = {
		material,
		depth,
		thickness,
		hasFeet: true,
		horizontals: new Map(),
		verticals: new Map(),
		...createCompartments(width, height, depth, thickness, material, api),
	};

	const verticals = createVerticals(baseState.compartments, baseState.thickness, baseState.width);

	const horizontals = createHorizontals(baseState.compartments, baseState.thickness, material, api);

	return { ...baseState, verticals, horizontals };
}

function getCompartmentPosition(index: number, totalIndex: number) {
	if (index === 0 && totalIndex === 1) {
		return CompartmentPosition.FirstAndLast;
	}

	if (index === 0) {
		return CompartmentPosition.First;
	}

	if (index + 1 === totalIndex) {
		return CompartmentPosition.Last;
	}

	return CompartmentPosition.Surrounded;
}

function createCompartments(
	width: number,
	height: number,
	depth: number,
	thickness: number,
	material: string,
	api?: ApiData,
) {
	const current = getMaterialThickness(material, thickness, api);
	const maxPanelWidth = current?.parts.horizontal.constraints.height.max ?? 0;
	const maxPanelLength = current?.parts.horizontal.constraints.width.max ?? 0;

	const compartments: ObjectList<CompartmentType> = new Map();
	const amountRows = Math.ceil(height / maxPanelWidth);
	const amountCols = Math.ceil(width / maxPanelLength) + 1;
	const outerWidthPerCompartment = Math.round(width / amountCols);
	const outerHeightPerCompartment = Math.round(height / amountRows);

	const cabinetWidthCorrection = width - outerWidthPerCompartment * amountCols;
	const cabinetHeightCorrection = height - outerHeightPerCompartment * amountRows;

	for (const column of Array(amountCols).keys()) {
		for (const row of Array(amountRows).keys()) {
			const columnPosition = getCompartmentPosition(column, amountCols);
			const rowPosition = getCompartmentPosition(row, amountRows);

			const xCorrection = match(columnPosition)
				.with(CompartmentPosition.First, CompartmentPosition.FirstAndLast, () => thickness)
				.otherwise(() => thickness / 2);

			const yCorrection = match(rowPosition)
				.with(CompartmentPosition.First, CompartmentPosition.FirstAndLast, () => thickness)
				.otherwise(() => thickness / 2);

			let widthCorrection = match(columnPosition)
				.with(CompartmentPosition.First, CompartmentPosition.Last, () => thickness + thickness / 2)
				.with(CompartmentPosition.FirstAndLast, () => thickness * 2)
				.otherwise(() => thickness);

			if (column === amountCols - 1) {
				widthCorrection -= cabinetWidthCorrection;
			}

			let heightCorrection = match(rowPosition)
				.with(CompartmentPosition.First, CompartmentPosition.Last, () => thickness + thickness / 2)
				.with(CompartmentPosition.FirstAndLast, () => thickness * 2)
				.otherwise(() => thickness);

			if (row === amountRows - 1) {
				heightCorrection -= cabinetHeightCorrection;
			}

			const compartment = updateCompartment(
				{
					id: generateId(IdPrefix.Compartment),
					x: column * outerWidthPerCompartment + xCorrection,
					y: outerHeightPerCompartment * row + yCorrection,
					z: 0,
					width: outerWidthPerCompartment - widthCorrection,
					depth: depth,
					height: outerHeightPerCompartment - heightCorrection,
					background: undefined,
					door: null,
					shelves: [],
				},
				thickness,
			);

			compartments.set(compartment.id, compartment);
		}
	}

	return {
		compartments,
		width,
		height,
	};
}

export function createVerticals(
	compartments: ObjectList<CompartmentType>,
	thickness: number,
	width: number,
) {
	const verticals: ObjectList<Vertical> = new Map();

	for (const compartment of compartments.values()) {
		for (const vertical of verticalsFromCompartment(compartment, compartments, thickness, width)) {
			verticals.set(vertical.id, vertical);
		}
	}

	return verticals;
}

export function createHorizontals(
	compartments: ObjectList<CompartmentType>,
	thickness: number,
	material: string,
	api?: ApiData,
) {
	const current = getMaterialThickness(material, thickness, api);
	const maxPanelLength = current?.parts.horizontal.constraints.width.max ?? 0;

	const horizontals: ObjectList<Horizontal> = new Map();

	const availableCompartments = [...compartments.values()];

	availableCompartments.sort((a, b) => a.x - b.x);

	const rows: Array<Array<CompartmentType>> = [];

	// loop every compartment to create rows of compartments that are next to each other
	while (availableCompartments.length > 0) {
		const row: Array<CompartmentType> = [];

		let compartment = availableCompartments.shift()!;

		if (!compartment) {
			break;
		}

		row.push(compartment);

		while (compartment) {
			const index = availableCompartments.findIndex(
				(c) =>
					c.x === compartment.x + compartment.width + thickness &&
					c.y + c.height === compartment.y + compartment.height,
			);

			if (index === -1) break;

			const next = availableCompartments.splice(index, 1).at(0);

			if (!next) {
				break;
			}

			compartment = next;
			row.push(compartment);
		}

		rows.push(row);
	}

	// create fake compartments for the bottom of the row
	const bottoms = [...compartments.values()]
		.filter((c) => c.y === thickness)
		.map((c) => ({ ...c, y: c.y - c.height - thickness }));

	rows.push(bottoms);

	for (const row of rows) {
		row.sort((a, b) => a.x - b.x);

		if (row.length === 0) {
			continue;
		}

		let next: CompartmentType = row.shift()!;

		const addition = row.length === 0 ? thickness : thickness / 2;

		let length = next.width + thickness + addition;
		let x = next.x - thickness;

		if (row.length === 0) {
			const id = generateId(IdPrefix.Horizontal);
			horizontals.set(id, {
				id,
				x,
				y: next.y + next.height,
				z: 0,
				width: length,
				height: thickness,
				depth: next.depth,
			});
		}

		while (row.length > 0) {
			next = row.shift()!;

			const addition = row.length === 0 ? thickness : 0;
			const newLength = length + next.width + thickness + addition;

			// create a new horizontal when the length is too long
			if (newLength > maxPanelLength) {
				const id = generateId(IdPrefix.Horizontal);
				horizontals.set(id, {
					id,
					x,
					y: next.y + next.height,
					z: 0,
					width: length,
					height: thickness,
					depth: next.depth,
				});

				length = next.width + thickness + addition;
				x = next.x - thickness / 2;
			} else {
				length = newLength;
			}

			// create the last horizontal
			if (row.length === 0 && length > 0) {
				const id = generateId(IdPrefix.Horizontal);
				horizontals.set(id, {
					id,
					x: x,
					y: next.y + next.height,
					z: 0,
					width: length - thickness / 2,
					height: thickness,
					depth: next.depth,
				});
			}
		}
	}

	return horizontals;
}

export const serialize = (state: CabinetState) => {
	return JSON.stringify({
		material: state.material,
		thickness: state.thickness,
		width: state.width,
		height: state.height,
		depth: state.depth,
		compartments: [...state.compartments.values()],
		verticals: [...state.verticals.values()],
		horizontals: [...state.horizontals.values()],
		version: CURRENT_VERSION,
		hasFeet: state.hasFeet,
	});
};

export const deserialize = (data: string): CabinetState => {
	return JSON.parse(data, (key, value) =>
		match(key)
			.with("compartments", () => new Map(value.map((i: CompartmentType) => [i.id, i])))
			.with("verticals", () => new Map(value.map((i: CompartmentType) => [i.id, i])))
			.with("horizontals", () => new Map(value.map((i: CompartmentType) => [i.id, i])))
			.otherwise(() => value),
	) as CabinetState;
};

export const getCurrentMaterial = (state: CabinetState, api?: ApiData) => {
	return api?.materials.find((material) => {
		return material.thicknesses?.find((t) => t?.sku === state.material);
	});
};

export const getCurrentThickness = (state: CabinetState, api?: ApiData) => {
	return getCurrentMaterial(state, api)?.thicknesses?.find((thickness) => {
		return Number(thickness?.name) === state.thickness;
	});
};

export const getMaterialThickness = (material: string, thickness: number, api?: ApiData) => {
	return api?.materials
		.find((m) => m.thicknesses?.find((t) => t?.sku === material))
		?.thicknesses?.find((t) => Number(t?.name) === thickness);
};

export const isLongPartsBuildMethod = (thickness: number) => {
	return thickness === 18;
};

export const getCornerRounding = (state: CabinetState, api?: ApiData) => {
	return match(getCurrentThickness(state, api)?.sku)
		.with(...materialsSku.mdf_prime, () => 0.005)
		.otherwise(() => 0.002);
};

export const canSplitVertically = (
	compartment: CompartmentType,
	state: CabinetState,
	api?: ApiData,
) => {
	const minSize = getMinSize(compartment, "right", state, api);
	const newWidth = compartment.width / 2 + state.thickness / 2;

	return newWidth - state.thickness >= minSize;
};

const isAvailableToDistribute = (
	selectedCompartmentIds: string[],
	compartment: CompartmentType,
	state: CabinetState,
	layoutMode: LayoutModus,
) => {
	const compartments = selectedCompartmentIds.map((id) => state.compartments.get(id)!);

	const isLeftOrRight = compartments
		.filter((c) => compartmentsOnSameY(c, compartment))
		.some((c) => isNextToCompartmentOnX(c, compartment, state.thickness));

	const isTopOrBottom = compartments
		.filter((c) => compartmentsOnSameX(c, compartment))
		.some((c) => isNextToCompartmentOnY(c, compartment, state.thickness));

	if (layoutMode === LayoutModus.distributeHorizontal) {
		return isLeftOrRight && !isTopOrBottom;
	}

	if (layoutMode === LayoutModus.distributeVertical) {
		return !isLeftOrRight && isTopOrBottom;
	}

	return false;
};

export const canDistributeHorizontal = (
	selectedCompartmentIds: string[],
	compartment: CompartmentType,
	state: CabinetState,
) => {
	if (selectedCompartmentIds.length === 0) return true;

	return isAvailableToDistribute(
		selectedCompartmentIds,
		compartment,
		state,
		LayoutModus.distributeHorizontal,
	);
};

export const canDistributeVertical = (
	selectedCompartmentIds: string[],
	compartment: CompartmentType,
	state: CabinetState,
) => {
	if (selectedCompartmentIds.length === 0) return true;

	return isAvailableToDistribute(
		selectedCompartmentIds,
		compartment,
		state,
		LayoutModus.distributeVertical,
	);
};

export const canSplitHorizontally = (
	compartment: CompartmentType,
	state: CabinetState,
	api?: ApiData,
) => {
	const minSize = getMinSize(compartment, "bottom", state, api);
	const newHeight = compartment.height / 2 + state.thickness / 2;

	return newHeight - state.thickness >= minSize;
};

export const canAddShelf = (compartment: CompartmentType) => {
	const hasBlindVertical = getShouldHaveBlindVertical(compartment);

	const maxShelves = getMaxShelfAmount(compartment);
	const maxAmount = hasBlindVertical ? maxShelves * 2 : maxShelves;
	const backgroundZIsAtZero = compartment.background ? compartment.background.z === 0 : true;

	return compartment.shelves.length < maxAmount && backgroundZIsAtZero;
};

export const getShouldHaveBlindVertical = (compartment: CompartmentType) => {
	return compartment.door?.type === "double" ? compartment.width > VERTICAL_BLIND_THRESHOLD : false;
};

export const canAddDoor = (
	compartment: CompartmentType,
	side: DoorSide,
	state: CabinetState,
	api?: ApiData,
) => {
	const thickness = getCurrentThickness(state, api);

	const { width, depth } = thickness?.parts.door.constraints || {};
	const { min: minWidth = 0, max: maxWidth = 0 } = width || {};
	const { min: minDepth = 0, max: maxDepth = 0 } = depth || {};

	// this is the minimum height for a vertical to support a hinge
	const hasVerticalsInTheWay = [...state.verticals].some(([_id, vertical]) => {
		const isOnSameX =
			side === "left"
				? compartment.x === vertical.x + vertical.height
				: compartment.x + compartment.width === vertical.x;

		return (
			isOnSameX &&
			vertical.y >= compartment.y &&
			vertical.y + vertical.width <= compartment.y + compartment.height &&
			vertical.width <= MIN_HEIGHT_FOR_VERTICAL
		);
	});

	const isWidthSmallerThanHeight = compartment.width < compartment.height;

	const maxShelves = Math.ceil(
		(compartment.height - SHELF_OFFSET_WITH_DOOR - SHELF_OFFSET_WITH_DOOR) / SHELF_DISTANCE,
	);
	const canAddDoorWithShelfs = compartment.shelves.length
		? compartment.shelves.length < maxShelves
		: true;

	const backgroundZIsAtZero = compartment.background ? compartment.background.z === 0 : true;

	const alreadyHasDoor =
		compartment.door?.type === "single" ? compartment.door.side === side : false;

	return (
		!hasVerticalsInTheWay &&
		isWidthSmallerThanHeight &&
		compartment.width >= Number(minWidth) &&
		compartment.width <= Number(maxWidth) &&
		compartment.height >= Number(minDepth) &&
		compartment.height <= Number(maxDepth) &&
		canAddDoorWithShelfs &&
		backgroundZIsAtZero &&
		!alreadyHasDoor
	);
};

export const canAddDoubleDoor = (
	compartment: CompartmentType,
	state: CabinetState,
	api?: ApiData,
) => {
	const thickness = getCurrentThickness(state, api);

	const { width, depth } = thickness?.parts.door.constraints || {};
	const { min: minWidth = 0, max: maxWidth = 0 } = width || {};
	const { min: minDepth = 0, max: maxDepth = 0 } = depth || {};

	// this is the minimum height for a vertical to support a hinge
	const hasVerticalsInTheWay = [...state.verticals].some(([_id, vertical]) => {
		const isOnSameX =
			compartment.x === vertical.x + vertical.height ||
			compartment.x + compartment.width === vertical.x;

		return (
			isOnSameX &&
			vertical.y >= compartment.y &&
			vertical.y + vertical.width <= compartment.y + compartment.height &&
			vertical.width <= MIN_HEIGHT_FOR_VERTICAL
		);
	});

	const isWidthSmallerThanHeight = compartment.width / 2 < compartment.height;

	const maxShelves = Math.ceil(
		(compartment.height - SHELF_OFFSET_WITH_DOOR - SHELF_OFFSET_WITH_DOOR) / SHELF_DISTANCE,
	);
	const canAddDoorsWithShelfs = compartment.shelves.length
		? compartment.shelves.length < maxShelves
		: true;

	const backgroundZIsAtZero = compartment.background ? compartment.background.z === 0 : true;

	const alreadyHasDoor = compartment.door?.type === "double";

	return (
		!hasVerticalsInTheWay &&
		isWidthSmallerThanHeight &&
		compartment.width >= Number(minWidth) * 2 &&
		compartment.width <= Number(maxWidth) * 2 &&
		compartment.width <= MAX_DOUBLE_DOOR_WIDTH &&
		compartment.height >= Number(minDepth) &&
		compartment.height <= Number(maxDepth) &&
		canAddDoorsWithShelfs &&
		backgroundZIsAtZero &&
		!alreadyHasDoor
	);
};

export const canChangeBackgroundDepth = (compartment: CompartmentType) => {
	const hasBlindVertical = getShouldHaveBlindVertical(compartment);

	if (compartment.background === undefined) return false;
	if (compartment.door !== null) return false;
	if (compartment.shelves.length > 0) return false;
	if (hasBlindVertical) return false;

	return true;
};
